import React, { useState, useEffect, useMemo } from "react";
import mainStyle from "../../../mainStyle.module.scss";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";
import { Box, Button, Typography, Tooltip, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isEqual, cloneDeep } from "lodash";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import CustomMenu from "components/Menu";
import ChipTag from 'components/ChipTag';

import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AlertIcon } from "assets/images/icons/icn_board_alert.svg";
import { ReactComponent as ExpandIcon } from "assets/images/icons/btn_topology_expand.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_btn_bin.svg";

import useAuthScope from "hooks/useAuthScope";

// Const
import { formLimitation } from 'const';

// Helper
import {
  getPortTagsRequestParams,
  getPortTagsCreatePayload,
  initChips,
} from "./helper";

// Utils
import {
  calcVlanIdsCountByDevice
} from 'utils/validate';

import {
  getApProfileOverrideValues
} from '../../helper/main';


const AccessPointEditModal = ({
  setAlertInfo,
  openAPEditModal,
  setOpenAPEditModal,
  handleCloseAPEditModal,
  selectedRows,
  originalRowData,
  setOriginalRowData,
  accessPointsData,
  setAccessPointsData,
  onUpdateBlockerData,
  profileAPList,
  initCallback,
  onEditCallback,
}) => {
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const uuids = useMemo(() => {
    return {
      portTags: {
        read: uuidv4(),
        create: uuidv4(),
      },
      chipTags: {
        update: uuidv4(),
      }
    }
  }, []);

  const [searchValue, setSearchValue] = useState("");
  const [removeAnchorEl, setRemoveAnchorEl] = useState(null);
  const [addAnchorEl, setAddAnchorEl] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isEdited, setIsEdited] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [originalEditValues, setOriginalEditValues] = useState({});
  const [selectedProfile, setSelectedProfile] = useState(null);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault, [selectedProfile?.isDefault]);

  const sameModelPorts = useMemo(() => accessPointsData.filter(loopPort => loopPort.modelName === editedValues.modelName), [accessPointsData, editedValues]);
  const vlanIdsCount = useMemo(() => calcVlanIdsCountByDevice(sameModelPorts, editedValues), [sameModelPorts, editedValues]);

  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const [isChanged, setIsChanged] = useState(false);

  const [originalPortChips, setOriginalPortChips] = useState([]);
  const [portChips, setPortChips] = useState([]);
  const [portTags, setPortTags] = useState([]);

  const theme = useTheme();
  const deviceNames =
    selectedRows?.map?.((row) => row.original.deviceName) || [];

  const extraCount = deviceNames.length > 1 ? deviceNames.length - 1 : 0;

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    ml: "50px",
    mt: "20px",
  };

  const getInputStyles = (field) => ({
    width: "300px",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,

      "& .MuiOutlinedInput-notchedOutline": {
        border: isEdited[field]
          ? `4px solid ${theme.palette.primary.orange}`
          : `1px solid ${theme.palette.primary.borderGreyPrimary}`,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: isEdited[field]
          ? `4px solid ${theme.palette.primary.orange}`
          : `1px solid ${theme.palette.primary.mainHover}`,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: isEdited[field]
          ? `4px solid ${theme.palette.primary.orange}`
          : `1px solid ${theme.palette.primary.main}`,
      },
    },
  });

  const handleRemoveClick = (event, tag) => {
    setRemoveAnchorEl(event.currentTarget);
    setSelectedTag(tag);
  };

  const handleRemoveClose = () => {
    setRemoveAnchorEl(null);
    setSelectedTag(null);
  };

  const handleRemove = (tagToRemove) => {
    setEditedValues((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter(
        (t) =>
          (typeof t === "string" ? t : t.value) !==
          (typeof tagToRemove === "string" ? tagToRemove : tagToRemove.value)
      ),
    }));

    setIsEdited((prev) => ({ ...prev, tags: true }));
    handleRemoveClose();
  };

  const handleAddClick = (event) => setAddAnchorEl(event.currentTarget);

  const handleAddClose = () => {
    setAddAnchorEl(null);
    setSearchValue("");
  };

  const handleAdd = (newTag) => {
    if (!newTag) return;

    setEditedValues((prev) => {
      const normalizedTags = (prev.tags || []).map((t) =>
        typeof t === "string" ? { value: t, isNew: false } : t
      );

      return {
        ...prev,
        tags: [...normalizedTags, { value: newTag, isNew: true }],
      };
    });

    setIsEdited((prev) => ({ ...prev, tags: true }));
    handleAddClose();
  };

  const onSave = () => {
    const updatedPortsMap = getUpdatedPortsMap();
    const newAccessPointsPortsData = accessPointsData.map(loopAP => {
      const matchedAP = updatedPortsMap[loopAP.id];

      if (matchedAP) {
        return {
          ...matchedAP,
          isWaitingEdit: true
        }
      } else {
        return loopAP;
      }
    })

    setAccessPointsData(cloneDeep(newAccessPointsPortsData));
    onUpdateBlockerData(newAccessPointsPortsData);

    const updatedOriginalPorts = getUpdatedOriginalPorts();
    setOriginalRowData(updatedOriginalPorts);

    setIsChanged(false);
    setOpenAPEditModal(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

    onEditCallback(editedValues);

  };

  const getUpdatedPortsMap = () => {
    const updatedPortsMap = Object.fromEntries(selectedRows.map(row => {
      const original = row.original;
      const combinedEditedValues = {
        ...original,
        ...editedValues,
      }

      const updatedPort = {
        ...combinedEditedValues,
        id: original.id,
        portIndex: original.portIndex,
        macAddress: original.macAddress,
        deviceName: original.deviceName,
        portName: original.portName,
        portTags: portChips.map(chip => chip.tagId),
        portProfile: selectedProfile?.value ?? combinedEditedValues.portProfile,
        operation: {
          type: combinedEditedValues.operation?.type,
          mirrorPort: combinedEditedValues.operation?.mirrorPort ?? [],
          mirrorMode: combinedEditedValues.operation?.mirrorMode ?? "",
          aggregatePort: combinedEditedValues.operation?.aggregatePort ?? [],
          aggregateType: combinedEditedValues.operation?.aggregateType ?? "",
        },
      }

      return [updatedPort.id, updatedPort];
    }));

    return updatedPortsMap;
  };

  const getUpdatedOriginalPorts = () => {
    const updatedOriginalPorts = selectedRows.map(row => {
      return { ...row.original, ...editedValues };
    })

    return updatedOriginalPorts;
  }

  const handleCancel = () => {
    if (selectedRows.length > 0) {
      setEditedValues(selectedRows[0].original);
      setOriginalEditValues(selectedRows[0].original);
      setPortChips(originalPortChips);

      const matchedProfile = profileAPList.find(profile => profile.value === selectedRows[0].original.portProfile) || null;
      setSelectedProfile(matchedProfile);
    } else {
      setEditedValues({});
      setOriginalEditValues({});
      setSelectedProfile(null);
    }

    setIsEdited({});
    setIsChanged(false);
  };

  const updateEditedValues = (field, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (!isEqual(originalEditValues[field], value)) {
      setIsEdited((prev) => ({ ...prev, [field]: true }));
      setIsChanged(true);
    } else {
      setIsEdited((prev) => {
        const { [field]: removedField, ...rest } = prev;
        return rest;
      });
      setIsChanged(false);
    }
  };

  const checkAllowVlans = (value) => {
    if (!value?.trim()) return false;

    const parts = value.split(",").map(s => s.trim());
    const used = new Set();
    const { min, max } = formLimitation.vlanId;

    for (let part of parts) {
      if (part === "") return false;

      if (part.includes("-")) {
        const [startStr, endStr] = part.split("-").map(s => s.trim());
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);

        if (
          isNaN(start) || isNaN(end) ||
          start < min || end > max ||
          start > end
        ) return false;

        for (let i = start; i <= end; i++) {
          if (used.has(i)) return false;
          used.add(i);
        }

      } else {
        const num = parseInt(part, 10);
        if (isNaN(num) || num < min || num > max) return false;

        if (used.has(num)) return false;
        used.add(num);
      }
    }

    return true;
  }

  const isFormInvalid = () => {
    let isInvalid = false;
    const { PVID, allowVlan } = editedValues;

    if (vlanIdsCount.number > 128) isInvalid = true;
    if (!PVID || +PVID < 1 || +PVID > 4094) isInvalid = true;
    if (!checkAllowVlans(allowVlan)) isInvalid = true;

    return isInvalid;
  }

  const fetchPortTags = () => {
    if (isAuthReady) {
      const { apiPaths, restfulParams } = getPortTagsRequestParams({ auth, method: "read" });

      sendRequest(
        apiPaths,
        uuids.portTags.read,
        restfulParams
      );
    }
  }

  const createPortTags = newTag => {
    const { apiPaths, restfulParams } = getPortTagsRequestParams({ auth, method: "create" });
    const payload = getPortTagsCreatePayload({ tags: newTag });

    sendRequest(
      apiPaths,
      uuids.portTags.create,
      restfulParams,
      payload
    );
  }

  const updateChips = newChip => {
    const updateChips = [...portChips, newChip];
    setPortChips(updateChips);
  }

  const removeChips = newChip => {
    const removedChips = portChips.filter(chip => chip.tagId !== newChip.tagId);
    setPortChips(removedChips);
  }

  useEffect(() => {
    if (!dataMap?.[uuids.portTags.read]) return;

    const portTagsResp = getData(uuids.portTags.read);
    const portTagsRespData = portTagsResp?.data ?? null;

    // Read tags
    if (portTagsRespData) {
      if (portTagsRespData.hasOwnProperty("code") && portTagsRespData.code !== 200) {
        // error handling
      } else {

        const chips = initChips({ chips: editedValues?.portTags, tags: portTagsRespData })
        setPortChips(chips);
        setOriginalPortChips(chips);
        setPortTags(portTagsRespData);
      }
    }

  }, [dataMap?.[uuids.portTags.read]]);

  useEffect(() => {
    // Create tags
    if (!dataMap?.[uuids.portTags.create]) return;

    const creatingResponse = getData(uuids.portTags.create);
    if (creatingResponse) {
      fetchPortTags();
    }
  }, [dataMap?.[uuids.portTags.create]]);

  useEffect(() => {
    // Update chips
    if (!dataMap?.[uuids.chipTags.update]) return;

    const updatingResponse = getData(uuids.chipTags.update);
    if (updatingResponse) {
      initCallback();
    }
  }, [dataMap?.[uuids.chipTags.update]]);

  useEffect(() => {
    if (openAPEditModal && selectedRows.length > 0) {
      setEditedValues(selectedRows[0].original);
      setOriginalEditValues(selectedRows[0].original);
      setIsEdited({});
      setIsChanged(false);
      fetchPortTags();

      const matchedProfile = profileAPList.find(profile => profile.value === selectedRows[0].original.portProfile) || null;
      setSelectedProfile(matchedProfile);
    }
  }, [openAPEditModal, selectedRows]);

  return (
    <>
      <ModalContainer display={openAPEditModal} modalWidth="auto">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              mb: "30px",
              ml: "20px",
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Selected Ports
          </Typography>
          <Button
            onClick={handleCloseAPEditModal}
            sx={{
              marginRight: "-10px",
              marginTop: "-28px",
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              backgroundColor: theme.palette.primary.subBlue,
              borderRadius: theme.shape.customBorderRadius[0],
              gap: "10px",
              padding: "4px 8px",
            }}
          >
            {editedValues
              ? `${editedValues.deviceName}/${editedValues.portIndex}`
              : "No device selected"}
          </Typography>

          {extraCount > 0 && (
            <Tooltip
              title={
                <Box
                  className={mainStyle["custom-scrollbar"]}
                  sx={{
                    maxHeight: 250,
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {deviceNames.map((name, idx) => {
                    const row = selectedRows[idx]?.original;
                    return (
                      <Typography
                        sx={{
                          color: theme.palette.primary.withe,
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.customFontSize[1],
                          fontWeight: theme.typography.customFontWeight[3],
                          lineHeight: 1.92,
                        }}
                        key={idx}
                      >
                        {`${row.deviceName}/${row.portIndex}`}
                      </Typography>
                    );
                  })}
                </Box>
              }
              arrow
            >
              <Typography
                sx={{
                  ml: 3,
                  color: theme.palette.primary.main,
                  fontSize: theme.typography.customFontSize[10],
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                +{extraCount} Selected
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", ml: "20px", mt: "10px" }}
        >
          <AlertIcon />
          <Box
            sx={{
              ml: "10px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Only changes highlighted in{" "}
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.orange,
                fontWeight: "bold",
              }}
            >
              Orange
            </Box>{" "}
            will be applied to all selected ports.
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {editedValues && (
            <>
              {/* <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Name
                </Typography>
                <TextField
                  type="text"
                  value={editedValues.portName}
                  onChange={(e) =>
                    updateEditedValues("portName", e.target.value)
                  }
                  sx={getInputStyles("portName")}
                />
              </Box> */}
              {/*  <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Tag
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {editedValues.tags && editedValues.tags.length > 0 ? (
                    editedValues.tags.map((tag, idx) => {
                      const tagValue =
                        typeof tag === "string" ? tag : tag.value;
                      const isNew = typeof tag === "object" && tag.isNew;

                      return (
                        <span
                          key={idx}
                          style={{
                            minWidth: "46px",
                            height: "25px",
                            padding: "0 8px",
                            backgroundColor: isNew
                              ? theme.palette.primary.yellowPrimary
                              : theme.palette.primary.subBlue,
                            color: theme.palette.primary.main,
                            fontSize: theme.typography.customFontSize[9],
                            borderRadius: theme.shape.customBorderRadius[0],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={(e) => handleRemoveClick(e, tag)}
                        >
                          {tagValue}
                        </span>
                      );
                    })
                  ) : (
                    <span>-</span>
                  )}
                </Box>

                <CustomMenu
                  anchorEl={removeAnchorEl}
                  open={Boolean(removeAnchorEl)}
                  handleMenuClose={handleRemoveClose}
                  maxWidth={112}
                  height={41}
                  mt={2}
                  ml={8}
                  top="-12px"
                  right="78px"
                  borderLeftWidth="8px"
                  borderRightWidth="8px"
                  borderBottomWidth="12px"
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      color: theme.palette.primary.main,
                    }}
                    onClick={() => handleRemove(selectedTag)}
                  >
                    <RemoveIcon />
                    Remove
                  </Box>
                </CustomMenu>

                <Box sx={{ ml: 2, cursor: "pointer" }} onClick={handleAddClick}>
                  <ExpandIcon />
                </Box>

                <CustomMenu
                  anchorEl={addAnchorEl}
                  handleMenuClose={handleAddClose}
                  maxWidth={216}
                  height={268}
                  mt={2}
                  ml={22}
                  top="-12px"
                  right="174px"
                  borderLeftWidth="8px"
                  borderRightWidth="8px"
                  borderBottomWidth="12px"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.5,
                      borderBottom: `1px solid ${theme.palette.primary.mainHover}`,
                    }}
                  >
                    <TextField
                      variant="standard"
                      placeholder="Search..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      fullWidth
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: "14px" },
                      }}
                    />
                    <Box
                      onClick={() => handleAdd(searchValue)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ExpandIcon />
                    </Box>
                  </Box>
                </CustomMenu>
              </Box> */}

              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Profile
                </Typography>
                <MenuSelect
                  value={selectedProfile?.value || ""}
                  onChange={(opt) => {
                    const profile = profileAPList.find((item) => item.value === opt.value) || null;

                    setSelectedProfile(profile);
                    
                    const overrides = getApProfileOverrideValues(profile);
                    Object.entries(overrides).forEach(([key, value]) => {
                      updateEditedValues(key, value);
                    });
                  }}
                  options={profileAPList}
                  width="300px"
                  isEditedField={isEdited.portProfile}
                />
              </Box>
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Tags
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  {(!Array.isArray(portChips) || portChips.length === 0) && <Box sx={{ marginRight: 1 }}>-</Box>}
                  <ChipTag
                    isChipsEdit={false}
                    chips={portChips}
                    tags={portTags}
                    onChipsRemoveChange={props => {
                      const { removedChip } = props;
                      removeChips(removedChip)
                    }}
                    onChipsAddChange={props => {
                      const { newChip } = props;
                      updateChips(newChip);
                    }}
                    onTagsChange={props => {
                      const { newTag } = props;
                      createPortTags(newTag)
                    }}
                  />
                </Box>
              </Box>
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  PVID
                </Typography>
                {
                  isProfileMode &&
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled
                      type="number"
                      value={selectedProfile?.PVID ?? ""}
                      sx={getInputStyles("PVID")}
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greySecound,
                        fontSize: theme.typography.customFontSize[1],
                      }}
                    >
                      (1~4094)
                    </Typography>
                  </Box>
                }

                {
                  !isProfileMode &&
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable}
                      type="number"
                      value={editedValues.PVID ?? ""}
                      onChange={(e) => updateEditedValues("PVID", e.target.value)}
                      onBlur={(e) => {
                        const num = Number(e.target.value);
                        if (!isNaN(num)) updateEditedValues("PVID", num);
                      }}
                      sx={getInputStyles("PVID")}
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greySecound,
                        fontSize: theme.typography.customFontSize[1],
                      }}
                    >
                      (1~4094)
                    </Typography>
                  </Box>
                }
              </Box>

              {
                vlanIdsCount.number > 128 &&
                <>
                  <Box
                    sx={{
                      ...rowStyle,
                      marginTop: "0"
                    }}
                  >
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </Box>
                </>
              }

              {
                isProfileMode &&
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Allow VLANs
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled
                      type="text"
                      value={selectedProfile?.allowVlan}
                      sx={getInputStyles("allowVlan")}
                      placeholder="e.g. 1, 4-7"
                    />
                    <Typography
                      sx={{
                        color: theme.palette.primary.greySecound,
                        fontSize: theme.typography.customFontSize[1],
                      }}
                    >
                      {/* (1~4094) */}
                      {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    </Typography>
                  </Box>
                </Box>
              }

              {
                !isProfileMode &&
                <>
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Allow VLANs
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        disabled={!isEditable}
                        type="text"
                        value={editedValues.allowVlan}
                        onChange={(e) =>
                          updateEditedValues("allowVlan", e.target.value)
                        }
                        sx={getInputStyles("allowVlan")}
                        placeholder="e.g. 1, 4-7"
                      />
                      <Typography
                        sx={{
                          color: theme.palette.primary.greySecound,
                          fontSize: theme.typography.customFontSize[1],
                        }}
                      >
                        {/* (1~4094) */}
                        {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      </Typography>
                    </Box>
                  </Box>
                  {
                    vlanIdsCount.number > 128 &&
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "0"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  }
                </>
              }
            </>
          )}
        </Box>
        <Box
          sx={{
            paddingTop: "20px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
            onClick={handleCancel}
          >
            Revert
          </OutlineButton>
          <SolidButton
            width="100px"
            height="32px"
            disabled={isFormInvalid()}
            onClick={onSave}
          >
            Save
          </SolidButton>
        </Box>
      </ModalContainer>
    </>
  );
};

export default AccessPointEditModal;
